import { useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { emptyTableCellClassName } from '@/constants/ui'
import type { Company } from '@/types/company'
import { listCompanyUserCounts } from '@/api/companies'
import { formatPhoneDisplay } from '@/utils/phone'
import { CompanyStatusBadge } from '@/components/companies/CompanyStatusBadge'
import { LoadingSecondsBanner } from '@/components/ui/LoadingSecondsBanner'
import { Loader } from '@/components/ui/Loader'
import { prefetchCompanyDetail } from '@/utils/prefetch'

export function CompanyTable({
  companies,
  loading,
  onRowClick,
}: {
  companies: Company[]
  loading?: boolean
  onRowClick: (id: string) => void
}) {
  const { t } = useTranslation()
  const qc = useQueryClient()
  const ids = useMemo(() => companies.map((c) => c.id).sort(), [companies])
  const { data: countsMap } = useQuery<Record<string, number>>({
    queryKey: ['company-user-counts', ids],
    queryFn: () => listCompanyUserCounts(ids),
    enabled: ids.length > 0,
  })
  const userCount = (id: string) => countsMap?.[id] ?? 0
  const isInitialLoading = Boolean(loading) && companies.length === 0
  const isBackgroundLoading = Boolean(loading) && companies.length > 0

  return (
    <div className="overflow-x-auto rounded-xl border border-[var(--stryzh-color-border-default)]">
      {isBackgroundLoading ? <LoadingSecondsBanner /> : null}
      <table className="w-full text-left text-sm">
        <thead className="bg-[var(--stryzh-color-surface)] text-[var(--stryzh-color-text-secondary)]">
          <tr>
            <th className="px-3 py-2">{t('companies.table.name')}</th>
            <th className="px-3 py-2">{t('companies.table.type')}</th>
            <th className="px-3 py-2">{t('companies.table.inn')}</th>
            <th className="px-3 py-2">{t('companies.table.phone')}</th>
            <th className="px-3 py-2">{t('companies.table.users')}</th>
            <th className="px-3 py-2">{t('companies.table.status')}</th>
          </tr>
        </thead>
        <tbody>
          {isInitialLoading ? (
            <tr>
              <td colSpan={6} className={emptyTableCellClassName}>
                <Loader centered size="sm" />
              </td>
            </tr>
          ) : companies.length === 0 ? (
            <tr>
              <td colSpan={6} className={emptyTableCellClassName}>
                {t('common.noData')}
              </td>
            </tr>
          ) : (
            companies.map((c) => (
              <tr
                key={c.id}
                className="cursor-pointer border-t border-[color:var(--stryzh-color-border-default)] hover:bg-[color:var(--stryzh-color-border-subtle)]"
                onMouseEnter={() => {
                  qc.setQueryData(['company', c.id], c)
                  prefetchCompanyDetail(qc, c.id)
                }}
                onFocus={() => {
                  qc.setQueryData(['company', c.id], c)
                  prefetchCompanyDetail(qc, c.id)
                }}
                onClick={() => onRowClick(c.id)}
              >
                <td className="px-3 py-2 font-medium">{c.name}</td>
                <td className="px-3 py-2">
                  {t(`companyType.${c.type}`)}
                </td>
                <td className="px-3 py-2">{c.inn ?? '—'}</td>
                <td className="px-3 py-2">{formatPhoneDisplay(c.phone)}</td>
                <td className="px-3 py-2">{userCount(c.id)}</td>
                <td className="px-3 py-2">
                  <CompanyStatusBadge isActive={c.is_active} compact />
                </td>
              </tr>
            ))
          )}
        </tbody>
      </table>
    </div>
  )
}
